import {
  sampleDrawLayer,
  sampleLineLayer,
  samplePointLayer,
} from 'components/MapComponent/constants/sampleLayers';
import { apiPrefix } from 'constants/apiConfig';
import { LayerConfiguration } from './mapEventsEffectTypes';

export const hurricane_cones_layer = 'public.hurricane_cones';
export const hurricane_points_layer = 'public.hurricane_points';
export const hurricane_paths_layer = 'public.hurricane_paths';
export const hurricane_cones_layer_id = 'public.hurricane_cones_layer_id';
export const hurricane_points_layer_id = 'public.hurricane_points_layer_id';
export const hurricane_paths_layer_id = 'public.hurricane_paths_layer_id';
export const hurricane_cones_source_id = 'public.hurricane_cones_source_id';
export const hurricane_points_source_id = 'public.hurricane_points_source_id';
export const hurricane_paths_source_id = 'public.hurricane_paths_source_id';

function hurricaneLayerTypesToMapboxColorExpression() {
  return [
    'step',
    ['get', 'unified_speed'],
    '#0099FF',
    33,
    '#81C056',
    63,
    '#F6C445',
    82,
    '#EE7932',
    95,
    '#D64A27',
    112,
    '#AC32F6',
    136,
    '#D535A9',
  ];
}

export function addHurricaneLayers({
  addMapEventHandlers,
  addVectorSource,
  formattedDatetime,
  endDatetime,
  checkLayerToggle,
}: {
  addMapEventHandlers: (layerConfig: LayerConfiguration) => void;
  addVectorSource: (sourceId: string, tiles: string) => void;
  formattedDatetime: string;
  endDatetime: string;
  checkLayerToggle: (layerName: string) => boolean;
}) {
  const addHurricaneSource = (sourceId: string, layerString: string) => {
    const tileBaseUrl = `${apiPrefix}/event_tiles/${layerString}/{z}/{x}/{y}.pbf`;
    const tilePath = `${tileBaseUrl}?datetime_selected=${formattedDatetime}&end_datetime=${endDatetime}`;
    addVectorSource(sourceId, tilePath);
  };

  if (checkLayerToggle('Hurricane')) {
    addHurricaneSource(hurricane_cones_source_id, hurricane_cones_layer);
    addHurricaneSource(hurricane_paths_source_id, hurricane_paths_layer);
    addHurricaneSource(hurricane_points_source_id, hurricane_points_layer);

    const hurricaneConesLayerConfig = {
      ...sampleDrawLayer,
      id: hurricane_cones_layer_id,
      source: hurricane_cones_source_id,
      'source-layer': hurricane_cones_layer,
      generateId: true,
      paint: {
        ...sampleDrawLayer.paint,
        'fill-color': '#FFFFFF',
      },
      metadata: {
        eventlayer: true,
        usermade: undefined,
      },
    };

    const hurricanePathsLayerConfig = {
      ...sampleLineLayer,
      id: hurricane_paths_layer_id,
      source: hurricane_paths_source_id,
      'source-layer': hurricane_paths_layer,
      generateId: true,
      paint: {
        ...sampleLineLayer.paint,
        'line-color': '#D3D3D3',
      },
      metadata: {
        eventlayer: true,
        usermade: undefined,
      },
    };

    const hurricanePointsLayerConfig = {
      ...samplePointLayer,
      id: hurricane_points_layer_id,
      source: hurricane_points_source_id,
      'source-layer': hurricane_points_layer,
      generateId: true,
      paint: {
        ...samplePointLayer.paint,
        'circle-color': hurricaneLayerTypesToMapboxColorExpression(),
        'circle-radius': 10,
      },
      metadata: {
        eventlayer: true,
        usermade: undefined,
      },
    };

    // Add event handlers for hurricane layers
    [
      hurricaneConesLayerConfig,
      hurricanePathsLayerConfig,
      hurricanePointsLayerConfig,
    ].forEach((layerConfig) => {
      addMapEventHandlers(layerConfig);
    });
  }
}
export function updateHurricaneFilters(
  layerExists: (layerId: UUID) => boolean,
  checkLayerToggle: (layerName: string) => boolean,
  setFilter: (layerId: UUID, filter: any[]) => void
) {
  if (layerExists(hurricane_cones_layer_id)) {
    const hurricanesAreOn = checkLayerToggle('Hurricane');
    const hurricaneFilter = hurricanesAreOn
      ? ['!', ['has', 'hazard_data_category']]
      : ['has', 'hazard_data_category'];
    setFilter(hurricane_cones_layer_id, hurricaneFilter);
    setFilter(hurricane_paths_layer_id, hurricaneFilter);
    setFilter(hurricane_points_layer_id, hurricaneFilter);
  }
}

export function removeHurricaneLayers({
  removeLayer,
  removeSource,
}: {
  removeLayer: (layerId: string) => void;
  removeSource: (sourceId: string) => void;
}) {
  removeLayer(hurricane_cones_layer_id);
  removeLayer(hurricane_paths_layer_id);
  removeLayer(hurricane_points_layer_id);
  removeSource(hurricane_cones_source_id);
  removeSource(hurricane_paths_source_id);
  removeSource(hurricane_points_source_id);
}

export function setHurricaneLayersVisibility({
  setLayerVisibility,
  isVisible,
}: {
  setLayerVisibility: (layerId: string, isVisible: boolean) => void;
  isVisible: boolean;
}) {
  setLayerVisibility(hurricane_cones_layer_id, isVisible);
  setLayerVisibility(hurricane_paths_layer_id, isVisible);
  setLayerVisibility(hurricane_points_layer_id, isVisible);
}
